/**
 * TNE 2026, © Mika (Mikaël FOURNY) pour la DRANE Nancy-Metz.
 * src/stores/toast.ts
 *
 * Rôle :
 *   Store Zustand minimal pour gérer la queue des toasts (notifications
 *   éphémères en bas/haut d'écran). API impérative depuis n'importe quel
 *   composant : `useToastStore.getState().push({ kind: 'success', … })`.
 *
 * Réutilisable :
 *   100 %. Aucun couplage TNE, pattern store + queue typique pour toute
 *   app React.
 *
 * Voir : src/components/ui/Toast.tsx (rendu) et src/components/ui/Toast.module.css.
 *
 * @author Mika
 * @since 2026-05-04
 */

import { create } from 'zustand';

/** Catégorie visuelle du toast (couleur + icône). */
export type ToastKind = 'success' | 'info' | 'warning' | 'error';

export interface ToastItem {
  /** Identifiant unique (généré automatiquement par push()). */
  id: string;
  kind: ToastKind;
  /** Message court, le wording vient de i18n/fr.ts côté caller. */
  message: string;
  /** Durée d'affichage en ms. Défaut 4000. 0 = persistant (close manuel). */
  durationMs?: number;
}

interface ToastState {
  items: ToastItem[];
  /** Ajoute un toast à la queue (renvoie son id pour pouvoir l'annuler). */
  push: (toast: Omit<ToastItem, 'id'>) => string;
  /** Retire un toast par id. */
  dismiss: (id: string) => void;
  /** Vide tous les toasts. */
  clear: () => void;
}

/**
 * Génère un identifiant lisible, pas critique sécurité, pas besoin d'UUID
 * cryptographique. `crypto.randomUUID()` est dispo partout en 2026 mais on
 * laisse une option dégradée pour les vieux Edge.
 */
function makeId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return Math.random().toString(36).slice(2) + Date.now().toString(36);
}

export const useToastStore = create<ToastState>((set, get) => ({
  items: [],
  push: (toast) => {
    const id = makeId();
    const item: ToastItem = { id, ...toast };
    set((s) => ({ items: [...s.items, item] }));

    // Auto-dismiss timer (sauf si durationMs === 0).
    const duration = toast.durationMs ?? 4000;
    if (duration > 0) {
      window.setTimeout(() => {
        get().dismiss(id);
      }, duration);
    }

    return id;
  },
  dismiss: (id) => {
    set((s) => ({ items: s.items.filter((i) => i.id !== id) }));
  },
  clear: () => set({ items: [] }),
}));

// ──────────────────────────────────────────────────────────────────────────
// Helper raccourci (sucre syntaxique pour les call sites) :
//
//   toast.success('Inscription confirmée');
//   toast.error('Erreur réseau');
//
// L'objet `toast` est exporté en plus de `useToastStore` pour une DX
// confortable. Réutilisable tel quel.
// ──────────────────────────────────────────────────────────────────────────

export const toast = {
  success: (message: string, durationMs?: number) =>
    useToastStore.getState().push({ kind: 'success', message, durationMs }),
  info: (message: string, durationMs?: number) =>
    useToastStore.getState().push({ kind: 'info', message, durationMs }),
  warning: (message: string, durationMs?: number) =>
    useToastStore.getState().push({ kind: 'warning', message, durationMs }),
  error: (message: string, durationMs?: number) =>
    useToastStore.getState().push({ kind: 'error', message, durationMs }),
};
